import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { AllowedHttpMethods } from '~/features/agent-forge/types';
import { intlService } from '~/i18n/IntlService';
import { api } from '~/redux/api/createApi';

const Page = () => {
  const intl = useIntl();
  const [agentForgeUrl, setAgentForgeUrl] = useState('');
  const [tenant, setTenant] = useState('');
  const [tenantEnv, setTenantEnv] = useState('');
  const [agentForgeHttpMethod, setAgentForgeHttpMethod] = useState('');

  // Optional fields
  const [agentForgePayload, setAgentForgePayload] = useState('');
  const [agentForgeHeaders, setAgentForgeHeaders] = useState('');
  const [jsonError, setJsonError] = useState<string>('');

  const [
    getAgentForgeInvocations,
    { data: responseData, isLoading: isInvokingAgentForge, error },
  ] = api.endpoints.getAgentForgeInvocations.useMutation();

  const handleSubmit = async () => {
    if (!agentForgeUrl.trim() || !tenant.trim() || !tenantEnv.trim()) {
      return;
    }

    setJsonError('');

    try {
      const requestData: {
        agent_forge_url: string;
        tenant: string;
        tenant_env: string;
        agent_forge_payload?: Record<string, unknown>;
        agent_forge_headers?: Record<string, unknown>;
        agent_forge_http_method?: AllowedHttpMethods;
      } = {
        agent_forge_url: agentForgeUrl.trim(),
        tenant: tenant.trim(),
        tenant_env: tenantEnv.trim(),
      };

      if (agentForgePayload.trim()) {
        requestData.agent_forge_payload = JSON.parse(agentForgePayload.trim());
      }

      if (agentForgeHeaders.trim()) {
        requestData.agent_forge_headers = JSON.parse(agentForgeHeaders.trim());
      }

      requestData.agent_forge_http_method = agentForgeHttpMethod.trim()
        ? (agentForgeHttpMethod.trim() as AllowedHttpMethods)
        : 'POST';

      getAgentForgeInvocations(requestData);
    } catch (err) {
      setJsonError(
        err instanceof Error
          ? err.message
          : intlService.getIntl().formatMessage({
              id: 'agentForge.invalidJsonError',
              defaultMessage: 'Invalid JSON format',
              description: 'Error message for invalid JSON format',
            }),
      );
    }
  };

  const isFormValid = agentForgeUrl.trim() && tenant.trim() && tenantEnv.trim();

  return (
    <PageLayout
      title={intlService.getIntl().formatMessage({
        id: 'agentForge.pageTitle',
        defaultMessage: 'Agent Forge Invoke',
        description: 'Page title for Agent Forge invoke page',
      })}
    >
      <Layout spacing={4} styleProps={{ width: '100%' }}>
        <Card mode="bordered" width="50%">
          <Layout
            direction="column"
            spacing={4}
            styleProps={{ padding: '16px' }}
          >
            <FormField
              label={intl.formatMessage({
                id: 'AgentForgeInvokePage.agentForgeUrlLabel',
                defaultMessage: 'Agent Forge URL',
                description: 'Label for Agent Forge URL input field',
              })}
              required
              input={TextInput}
              name="agentForgeUrl"
              value={agentForgeUrl}
              onChange={(value) => setAgentForgeUrl(value as string)}
            />

            <FormField
              label={intl.formatMessage({
                id: 'AgentForgeInvokePage.tenantLabel',
                defaultMessage: 'Tenant',
                description: 'Label for tenant input field',
              })}
              required
              input={TextInput}
              name="tenant"
              value={tenant}
              onChange={(value) => setTenant(value as string)}
            />

            <FormField
              label={intl.formatMessage({
                id: 'AgentForgeInvokePage.tenantEnvironmentLabel',
                defaultMessage: 'Tenant Environment',
                description: 'Label for tenant environment input field',
              })}
              required
              input={TextInput}
              name="tenantEnv"
              value={tenantEnv}
              onChange={(value) => setTenantEnv(value as string)}
            />
            {/* Optional Fields */}
            <FormField
              label={intl.formatMessage({
                id: 'AgentForgeInvokePage.httpMethodLabel',
                defaultMessage: 'Agent Forge HTTP Method',
                description: 'Label for Agent Forge HTTP method input field',
              })}
              optional
              input={TextInput}
              name="agentForgeHttpMethod"
              value={agentForgeHttpMethod}
              onChange={(value) =>
                setAgentForgeHttpMethod(value as AllowedHttpMethods)
              }
            />
            <FormField
              label={intl.formatMessage({
                id: 'AgentForgeInvokePage.payloadLabel',
                defaultMessage: 'Agent Forge Payload (Optional)',
                description: 'Label for Agent Forge payload input field',
              })}
              optional
              input={TextArea}
              name="agentForgePayload"
              value={agentForgePayload}
              onChange={(value) => {
                setAgentForgePayload(value as string);
                setJsonError('');
              }}
              inputProps={{
                rows: 6,
              }}
            />

            <FormField
              label={intl.formatMessage({
                id: 'AgentForgeInvokePage.headersLabel',
                defaultMessage: 'Agent Forge Headers (Optional)',
                description: 'Label for Agent Forge headers input field',
              })}
              optional
              input={TextArea}
              name="agentForgeHeaders"
              value={agentForgeHeaders}
              onChange={(value) => {
                setAgentForgeHeaders(value as string);
                setJsonError('');
              }}
              inputProps={{
                rows: 6,
              }}
            />
            {jsonError && (
              <div style={{ color: '#c00', fontSize: '14px' }}>{jsonError}</div>
            )}
            <Button
              text={intl.formatMessage({
                id: 'AgentForgeInvokePage.invokeButton',
                defaultMessage: 'Invoke Agent Forge',
                description: 'Button text to invoke Agent Forge',
              })}
              variant="primary"
              onClick={handleSubmit}
              disabled={isInvokingAgentForge || !isFormValid}
            />
          </Layout>
        </Card>

        <Card
          header={{
            title: intlService.getIntl().formatMessage({
              id: 'agentForge.responseTitle',
              defaultMessage: 'Agent Forge Response',
              description: 'Title for Agent Forge response section',
            }),
          }}
          mode="bordered"
          width="50%"
        >
          <pre
            style={{
              padding: '16px',
              backgroundColor: error ? '#fee' : '#f5f5f5',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '500px',
              fontSize: '14px',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              marginTop: '8px',
              color: error ? '#c00' : 'inherit',
            }}
          >
            {JSON.stringify(
              responseData != null ? responseData : error,
              null,
              2,
            )}
          </pre>
        </Card>
      </Layout>
    </PageLayout>
  );
};

export default Page;
